import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://ship.gxpresss.com/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login/', credentials).then(res => res.data),
  register: (userData) => api.post('/auth/register/', userData).then(res => res.data),
  logout: () => api.post('/auth/logout/').then(res => res.data),
  logoutAll: () => api.post('/auth/logout-all/').then(res => res.data),
  getProfile: () => api.get('/auth/profile/').then(res => res.data),
  updateProfile: (profileData) => api.put('/auth/profile/update/', profileData).then(res => res.data),
  
  // Admin API
  getAdminDashboardStats: (params) => api.get('/auth/admin/dashboard-stats/', { params }).then(res => res.data),
  getPendingShipments: (params) => api.get('/auth/admin/pending-shipments/', { params }).then(res => res.data),
  getUsers: () => api.get('/auth/admin/users/').then(res => res.data),
  getCustomers: () => api.get('/auth/admin/users/').then(res => res.data),
  getUser: (userId) => api.get(`/auth/admin/users/${userId}/`).then(res => res.data),
  updateUser: (userId, data) => api.put(`/auth/admin/users/${userId}/`, data).then(res => res.data),
  deleteUser: (userId) => api.delete(`/auth/admin/users/${userId}/`).then(res => res.data),
  toggleUserStatus: (userId) => api.post(`/auth/admin/users/${userId}/toggle-status/`).then(res => res.data),
  getInvites: () => api.get('/auth/admin/invites/').then(res => res.data),
  createInvite: (inviteData) => api.post('/auth/admin/invites/', inviteData).then(res => res.data),
  resendInvite: (inviteId) => api.post(`/auth/admin/invites/${inviteId}/resend/`).then(res => res.data),
  deleteInvite: (inviteId) => api.delete(`/auth/admin/invites/${inviteId}/`).then(res => res.data),
  updateShipmentTracking: (shipmentId, data) => api.post(`/auth/admin/shipments/${shipmentId}/tracking/`, data).then(res => res.data),
  getAdminSettings: () => api.get('/auth/admin/settings/').then(res => res.data),
  updateAdminSettings: (data) => api.post('/auth/admin/settings/update/', data).then(res => res.data),
};

// Shipments API
export const shipmentsAPI = {
  getBatches: (params) => api.get('/shipments/batches/', { params }).then(res => res.data),
  getBatch: (id) => api.get(`/shipments/batches/${id}/`).then(res => res.data),
  uploadCSV: (formData) => api.post('/shipments/upload-csv/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }).then(res => res.data),
  createShipment: (data) => api.post('/shipments/create/', data).then(res => res.data),
  getMasterShipments: (params) => api.get('/shipments/masters/', { params }).then(res => res.data),
  getMasterShipment: (id) => api.get(`/shipments/masters/${id}/`).then(res => res.data),
  getChildShipments: (params) => api.get('/shipments/children/', { params }).then(res => res.data),
  getChildShipment: (id) => api.get(`/shipments/children/${id}/`).then(res => res.data),
  updateStatus: (type, id, data) => api.put(`/shipments/update-status/${type}/${id}/`, data).then(res => res.data),
  uploadTrackingNumbers: (formData) => api.post('/shipments/upload-tracking/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }).then(res => res.data),
  uploadLabel: (formData) => api.post('/shipments/upload-label/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }).then(res => res.data),
  uploadMasterLabel: (formData) => api.post('/shipments/upload-master-label/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }).then(res => res.data),
  uploadBoxLabel: (formData) => api.post('/shipments/upload-box-label/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }).then(res => res.data),
  uploadUnitLabel: (formData) => api.post('/shipments/upload-unit-label/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }).then(res => res.data),
  requestLabels: (shipmentId) => api.post('/shipments/request-labels/', { shipment_id: shipmentId }).then(res => res.data),
  proceedToPackaging: (shipmentId) => api.post('/shipments/proceed-to-packaging/', { shipment_id: shipmentId }).then(res => res.data),
  updateShipmentUnits: (shipmentId, units) => api.put('/shipments/update-shipment-units/', { 
    shipment_id: shipmentId, 
    units: units 
  }).then(res => res.data),
  savePackaging: (data) => api.post('/shipments/save-packaging/', data).then(res => res.data),
  
  // Warehouse management
  getOriginWarehouseShipments: () => api.get('/shipments/warehouse/origin/').then(res => res.data),
  getDestinationWarehouseShipments: () => api.get('/shipments/warehouse/destination/').then(res => res.data),
  scanBoxReceived: (boxId, warehouseType) => api.post('/shipments/warehouse/scan-box/', {
    box_tracking_number: boxId,
    warehouse_type: warehouseType
  }).then(res => res.data),
  uploadBoxTrackingNumbers: (boxesData) => api.post('/shipments/warehouse/upload-box-tracking/', {
    boxes: boxesData
  }).then(res => res.data),
  
  // Child shipment cancellation
  cancelChildShipment: (childShipmentId, reason) => api.post(`/shipments/children/${childShipmentId}/cancel/`, {
    cancellation_reason: reason
  }).then(res => res.data),
  getCancelledChildShipments: (masterShipmentId) => api.get(`/shipments/masters/${masterShipmentId}/cancelled-children/`).then(res => res.data),
  
};

// Tracking API
export const trackingAPI = {
  getEvents: (params) => api.get('/tracking/events/', { params }).then(res => res.data),
  getEvent: (id) => api.get(`/tracking/events/${id}/`).then(res => res.data),
  createEvent: (data) => api.post('/tracking/add-event/', data).then(res => res.data),
  searchTracking: (trackingNumber) => api.get('/tracking/search/', { 
    params: { tracking_number: trackingNumber } 
  }).then(res => res.data),
  getTrackingNumbers: (params) => api.get('/tracking/numbers/', { params }).then(res => res.data),
};

// Labels API
export const labelsAPI = {
  getTemplates: () => api.get('/labels/templates/').then(res => res.data),
  getTemplate: (id) => api.get(`/labels/templates/${id}/`).then(res => res.data),
  getLabels: (params) => api.get('/labels/', { params }).then(res => res.data),
  getLabel: (id) => api.get(`/labels/${id}/`).then(res => res.data),
  generateLabels: (data) => api.post('/labels/generate/', data).then(res => res.data),
  downloadLabels: (data) => api.post('/labels/download/', data, {
    responseType: 'blob'
  }).then(res => res.data),
  downloadSingleLabel: (id) => api.get(`/labels/download/${id}/`, {
    responseType: 'blob'
  }).then(res => res.data),
  getBatches: (params) => api.get('/labels/batches/', { params }).then(res => res.data),
  getBatch: (id) => api.get(`/labels/batches/${id}/`).then(res => res.data),
};

// Billing API
export const billingAPI = {
  // Billing profiles
  getProfiles: (params) => api.get('/billing/profiles/', { params }).then(res => res.data),
  getProfile: (id) => api.get(`/billing/profiles/${id}/`).then(res => res.data),
  createProfile: (data) => api.post('/billing/profiles/', data).then(res => res.data),
  updateProfile: (id, data) => api.put(`/billing/profiles/${id}/`, data).then(res => res.data),
  deleteProfile: (id) => api.delete(`/billing/profiles/${id}/`).then(res => res.data),
  
  // Customer assignments
  getCustomerAssignments: (params) => api.get('/billing/customer-assignments/', { params }).then(res => res.data),
  getCustomerAssignment: (id) => api.get(`/billing/customer-assignments/${id}/`).then(res => res.data),
  getCustomerBillingProfile: (customerId) => api.get(`/billing/customer/${customerId}/billing-profile/`).then(res => res.data),
  assignProfile: (data) => api.post('/billing/assign-profile/', data).then(res => res.data),
  updateCustomerAssignment: (id, data) => api.put(`/billing/customer-assignments/${id}/`, data).then(res => res.data),
  deleteCustomerAssignment: (id) => api.delete(`/billing/customer-assignments/${id}/`).then(res => res.data),
  
  // Bills
  getBills: (params) => api.get('/billing/bills/', { params }).then(res => res.data),
  getBill: (id) => api.get(`/billing/bills/${id}/`).then(res => res.data),
  createBill: (data) => api.post('/billing/bills/', data).then(res => res.data),
  updateBill: (id, data) => api.put(`/billing/bills/${id}/`, data).then(res => res.data),
  deleteBill: (id) => api.delete(`/billing/bills/${id}/`).then(res => res.data),
  generateBill: (data) => api.post('/billing/generate-bill/', data).then(res => res.data),
  markBillPaid: (id) => api.post(`/billing/bills/${id}/mark-paid/`).then(res => res.data),
  recalculateBill: (id) => api.post(`/billing/bills/${id}/recalculate/`).then(res => res.data),
  exportBill: (id) => api.get(`/billing/bills/${id}/export/`, { responseType: 'blob' }).then(res => res.data),
  getBillBreakdown: (id) => api.get(`/billing/bills/${id}/breakdown/`).then(res => res.data),
  getBillsByDateRange: (params) => api.get('/billing/bills-by-date-range/', { params }).then(res => res.data),
  
  // Bill approval
  approveBill: (billId) => api.post(`/billing/bills/${billId}/approve/`).then(res => res.data),
  
  // Extra charges
  getBillExtraCharges: (billId) => api.get(`/billing/bills/${billId}/extra-charges/`).then(res => res.data),
  addBillExtraCharge: (billId, data) => api.post(`/billing/bills/${billId}/extra-charges/`, data).then(res => res.data),
  updateBillExtraCharge: (billId, chargeId, data) => api.put(`/billing/bills/${billId}/extra-charges/${chargeId}/`, data).then(res => res.data),
  deleteBillExtraCharge: (billId, chargeId) => api.delete(`/billing/bills/${billId}/extra-charges/${chargeId}/`).then(res => res.data),
  
  // Reconciliations
  getBillReconciliations: (billId) => api.get(`/billing/bills/${billId}/reconciliations/`).then(res => res.data),
  addBillReconciliation: (billId, data) => api.post(`/billing/bills/${billId}/reconciliations/`, data).then(res => res.data),
  resolveReconciliation: (billId, reconciliationId, data) => api.put(`/billing/bills/${billId}/reconciliations/${reconciliationId}/resolve/`, data).then(res => res.data),
  
  // Statistics and utilities
  getStats: (params) => api.get('/billing/stats/', { params }).then(res => res.data),
  getUnassignedCustomers: () => api.get('/billing/unassigned-customers/').then(res => res.data),
  getBillBreakdown: (billId) => api.get(`/billing/bills/${billId}/breakdown/`).then(res => res.data),
  getCustomerBillingProfile: (customerId) => api.get(`/billing/customer/${customerId}/billing-profile/`).then(res => res.data),
};

// External Carrier Tracking API
export const externalTrackingAPI = {
  // Tracking requests
  getTrackingRequests: (params) => api.get('/tracking/external/requests/', { params }).then(res => res.data),
  getTrackingRequest: (id) => api.get(`/tracking/external/requests/${id}/`).then(res => res.data),
  createTrackingRequest: (data) => api.post('/tracking/external/requests/', data).then(res => res.data),
  updateTrackingRequest: (id, data) => api.put(`/tracking/external/requests/${id}/`, data).then(res => res.data),
  deleteTrackingRequest: (id) => api.delete(`/tracking/external/requests/${id}/`).then(res => res.data),
  
  // Track shipments
  trackShipment: (data) => api.post('/tracking/external/track/', data).then(res => res.data),
  getTrackingStatus: (trackingRequestId) => api.get(`/tracking/external/status/${trackingRequestId}/`).then(res => res.data),
  refreshTracking: (trackingRequestId) => api.post(`/tracking/external/refresh/${trackingRequestId}/`).then(res => res.data),
  getShipmentTracking: (childShipmentId) => api.get(`/tracking/external/shipment/${childShipmentId}/`).then(res => res.data),
  bulkTrackShipments: (data) => api.post('/tracking/external/bulk-track/', data).then(res => res.data),
};


// ... existing code ...



// Shipping API
export const shippingAPI = {
  // Address validation
  validateAddress: (data) => api.post('/shipping/validate-address/', data).then(res => res.data),
  getAddresses: (params) => api.get('/shipping/addresses/', { params }).then(res => res.data),
  getAddress: (id) => api.get(`/shipping/addresses/${id}/`).then(res => res.data),
  createGxpressAddress: () => api.post('/shipping/create-gxpress-address/').then(res => res.data),
  createDestinationAddress: (data) => api.post('/shipping/create-destination-address/', data).then(res => res.data),
  
  // Rate comparison
  compareRates: (data) => api.post('/shipping/compare-rates/', data).then(res => res.data),
  getRates: (params) => api.get('/shipping/rates/', { params }).then(res => res.data),
  getRate: (id) => api.get(`/shipping/rates/${id}/`).then(res => res.data),
  
  // Label generation
  generateLabel: (data) => api.post('/shipping/generate-label/', data).then(res => res.data),
  getLabels: (params) => api.get('/shipping/labels/', { params }).then(res => res.data),
  getLabel: (id) => api.get(`/shipping/labels/${id}/`).then(res => res.data),
  downloadLabel: (id) => api.get(`/shipping/labels/${id}/download/`, {
    responseType: 'blob'
  }).then(res => res.data),
  
  // Transactions
  getTransactions: (params) => api.get('/shipping/transactions/', { params }).then(res => res.data),
  getTransaction: (id) => api.get(`/shipping/transactions/${id}/`).then(res => res.data),
  
  // Analytics
  getAnalytics: (params) => api.get('/shipping/analytics/', { params }).then(res => res.data),
  getStats: (params) => api.get('/shipping/stats/', { params }).then(res => res.data),
  
  // Configuration
  getConfig: () => api.get('/shipping/config/').then(res => res.data),
  updateConfig: (data) => api.post('/shipping/config/update/', data).then(res => res.data),
  
  // Profitability analysis
  analyzeProfitability: (data) => api.post('/shipping/analyze-profitability/', data).then(res => res.data),
  
};

export default api;